import { logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { analytics } from '@/lib/firebase';
import { supabase } from '@/lib/supabase';
import type { Json } from '@/types/supabase';
import { logger } from '@/lib/logger';
import { getPreferences } from '@/services/ConsentService';

type AnalyticsEvent =
    | 'calculate_map'
    | 'save_map'
    | 'save_map_success'
    | 'save_map_error'
    | 'import_excel'
    | 'login'
    | 'logout'
    | 'sign_up'
    | 'view_personal_area'
    | 'paywall_view'
    | 'paywall_click'
    | 'feature_lock_view'
    | 'feature_lock_click'
    | 'save_attempt'
    | 'checkout_start'
    | 'checkout_modal_opened'
    | 'checkout_popup_blocked'
    | 'checkout_popup_opened'
    | 'checkout_popup_closed_manually'
    | 'checkout_error_returned'
    | 'payment_plan_selected'
    | 'checkout_plan_selected'
    | 'checkout_complete'
    | 'checkout_canceled'
    | 'ai_message_feedback'
    | 'page_view'
    | 'knowledge_section_toggle'
    | 'shabtai_resource_click'
    | 'jump_to_node'
    | 'graph_control_change'
    | 'theater_mode_toggle'
    | 'graph_center_refresh'
    | 'form_validation_error'
    | 'calculator_action'
    | 'help_content_view'
    | 'error'
    | 'exception'
    | 'map_created'
    | 'map_updated'
    | 'map_deleted'
    | 'merge_map_success'
    | 'share_map'
    | 'magic_link_hydration'
    | 'web_vital'
    | 'donation_click'
    | 'premium_button_click'
    | 'simulator_view'
    | 'simulator_search'
    | 'simulator_search_results'
    | 'simulator_preview_name'
    | 'simulator_mode_change'
    | 'simulator_compare_click'
    | 'simulator_pick'
    | 'simulator_rehydrate'
    | 'simulator_toggle_esoteric_badges'
    | 'compare_view'
    | 'compare_section_toggle'
    | 'compare_map_action'
    | 'app_reload'
    | 'focused_esoteric_select'
    | 'plg_overlay_locked'
    | 'plg_auth_conversion_started'
    | 'conversation_divergence_detected'
    | 'guest_results_refused_login'
    | 'chat_bridge_submitted'
    | 'chat_bridge_completed'
    | 'toggle_swipe_gesture'
    | 'subscription_cancel_requested';

/**
 * Interface for Analytics Providers
 * Allows swapping Firebase for other providers (PostHog, Mixpanel, etc.)
 */
interface AnalyticsAdapter {
    identifyUser(userId: string | null, traits?: Record<string, unknown>): void;
    trackEvent(eventName: string, params?: Record<string, unknown>): void;
    trackError(error: Error, context?: Record<string, unknown>): void;
}

/**
 * Firebase Implementation
 */
export class FirebaseAnalyticsAdapter implements AnalyticsAdapter {
    identifyUser(userId: string | null, traits?: Record<string, unknown>): void {
        if (analytics) {
            setUserId(analytics, userId);
            if (userId === null) {
                // Clear important traits on disconnect so subsequent anonymous events don't attach to the old user
                setUserProperties(analytics, { email: null });
            } else if (traits && Object.keys(traits).length > 0) {
                // setUserProperties allows filtering by user segments (e.g., subscription tier)
                setUserProperties(analytics, traits as { [key: string]: string });
            }
        }

        if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.log('[Analytics] Identify', userId, traits);
        }
    }

    trackEvent(eventName: string, params?: Record<string, unknown>): void {
        // Inject Global Context ("Where" & "When")
        const baseParams = {
            timestamp_utc: new Date().toISOString(),
            current_route: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
            app_version: import.meta.env.VITE_APP_VERSION || 'unknown'
        };

        const enrichedParams = { ...baseParams, ...params };

        if (!analytics) {
            this.logDev('[Mock Analytics] Event:', eventName, enrichedParams);
            return;
        }

        try {
            logEvent(analytics, eventName, enrichedParams);
            this.logDev('[Analytics] Event:', eventName, enrichedParams);
        } catch (error) {
            logger.warn('[Analytics] Failed to track event:', error);
        }
    }


    trackError(error: Error, context?: Record<string, unknown>): void {
        this.trackEvent('exception', {
            error_message: error.message,
            error_stack: error.stack?.substring(0, 500),
            timestamp: Date.now(),
            ...context
        });
    }

    private logDev(message: string, ...args: unknown[]) {
        if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.log(message, ...args);
        }
    }
}

export const ATTRIBUTION_STORAGE_KEY = 'spirit_acquisition_attribution';

export interface AttributionData {
    [key: string]: string | undefined;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
    referrer?: string;
    landing_path?: string;
    first_seen_at?: string;
}

/**
 * Main Service Singleton
 */
class AnalyticsService {
    private adapter: AnalyticsAdapter;
    private machineHash: string | null = null;
    private deviceProfile: Record<string, unknown> = {};
    private currentUserId: string | null = null;
    private eventCache = new Map<string, number>();
    private readonly CACHE_TTL = 2000; // 2 seconds
    private attribution: AttributionData | null = null;

    constructor(adapter: AnalyticsAdapter) {
        this.adapter = adapter;
        this.initMachineHash();
        this.initAttribution();
    }

    /**
     * Initializes First-Touch acquisition attribution from URL query parameters and document.referrer
     * Persists in sessionStorage to survive single-page client navigations
     */
    public initAttribution(): AttributionData | null {
        if (typeof window === 'undefined') return null;

        try {
            // 1. Read existing first-touch attribution from sessionStorage if already captured
            const cached = window.sessionStorage?.getItem(ATTRIBUTION_STORAGE_KEY);
            if (cached) {
                try {
                    this.attribution = JSON.parse(cached);
                    return this.attribution;
                } catch {
                    // Ignore parse error and proceed to extract fresh data
                }
            }

            // 2. Extract UTM parameters from current URL
            const search = window.location.search;
            const urlParams = new URLSearchParams(search);
            const utm_source = urlParams.get('utm_source') || undefined;
            const utm_medium = urlParams.get('utm_medium') || undefined;
            const utm_campaign = urlParams.get('utm_campaign') || undefined;
            const utm_term = urlParams.get('utm_term') || undefined;
            const utm_content = urlParams.get('utm_content') || undefined;

            // 3. Extract referrer (filter out same-origin self-referrals)
            const rawReferrer = typeof document !== 'undefined' ? document.referrer : '';
            let referrer: string | undefined = undefined;
            if (rawReferrer) {
                try {
                    const refUrl = new URL(rawReferrer);
                    if (refUrl.hostname !== window.location.hostname) {
                        referrer = rawReferrer;
                    }
                } catch {
                    referrer = rawReferrer;
                }
            }

            const data: AttributionData = {
                ...(utm_source && { utm_source }),
                ...(utm_medium && { utm_medium }),
                ...(utm_campaign && { utm_campaign }),
                ...(utm_term && { utm_term }),
                ...(utm_content && { utm_content }),
                ...(referrer && { referrer }),
                landing_path: window.location.pathname || '/',
                first_seen_at: new Date().toISOString()
            };

            this.attribution = data;

            // 4. Persist to sessionStorage as First-Touch attribution
            if (window.sessionStorage) {
                window.sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(data));
            }

            return this.attribution;
        } catch (err) {
            logger.warn('[Analytics] Failed to initialize attribution tracking', err);
            return null;
        }
    }

    public getAttribution(): AttributionData | null {
        if (!this.attribution) {
            this.initAttribution();
        }
        return this.attribution;
    }

    public setAttribution(data: Partial<AttributionData>): void {
        this.attribution = { ...(this.attribution || {}), ...data };
        if (typeof window !== 'undefined' && window.sessionStorage) {
            window.sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(this.attribution));
        }
    }

    private isBot(): boolean {
        if (typeof window === 'undefined') return false;

        // If we have a current user, we always treat it as a human (transparency first)
        if (this.currentUserId) return false;

        const userAgent = window.navigator.userAgent.toLowerCase();

        // Specialized Detection
        const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const isCI = /github|gitlab|jenkins|circleci|travis|vercel/i.test(userAgent);

        const botPatterns = [
            'googlebot', 'bingbot', 'yandexbot', 'duckduckbot', 'slurp',
            'baiduspider', 'ia_archiver', 'facebookexternalhit', 'facebot',
            'twitterbot', 'rogerbot', 'linkedinbot', 'embedly', 'quora link preview',
            'showyoubot', 'outbrain', 'pinterest/0.', 'slackbot', 'vkshare',
            'w3c_validator', 'redditbot', 'applebot', 'whatsapp', 'flipboard',
            'tumblr', 'bitlybot', 'skypeuripreview', 'nuzzel', 'discordbot',
            'google-read-aloud', 'qwantify', 'pinterestbot', 'bitrix link preview',
            'xing-content-u-proxy', 'azsocialshareer', 'crawler', 'spider', 'robot',
            'github', 'playwright', 'cypress', 'selenium', 'puppeteer', 'lighthouse', 'headless'
        ];

        const isAutomated = window.navigator.webdriver ||
            'domAutomation' in window ||
            'domAutomationController' in window;

        return botPatterns.some(pattern => userAgent.includes(pattern)) || !!isAutomated || isLocal || isCI;
    }

    private async initMachineHash() {
        if (typeof window === 'undefined') return;

        try {
            const nav = window.navigator as Navigator & { deviceMemory?: number; hardwareConcurrency?: number };
            const screen = window.screen;

            this.deviceProfile = {
                userAgent: nav.userAgent,
                language: nav.language,
                platform: nav.platform,
                vendor: nav.vendor,
                screenWidth: screen.width,
                screenHeight: screen.height,
                devicePixelRatio: window.devicePixelRatio,
                hardwareConcurrency: nav.hardwareConcurrency || 0,
                deviceMemory: nav.deviceMemory || 0,
                maxTouchPoints: nav.maxTouchPoints || 0
            };

            const components = [
                this.deviceProfile.userAgent,
                this.deviceProfile.screenWidth,
                this.deviceProfile.screenHeight,
                this.deviceProfile.hardwareConcurrency,
                this.deviceProfile.deviceMemory,
                this.deviceProfile.platform
            ].join('|');

            // Defensive guard for window.crypto.subtle (missing in non-secure contexts or JSDOM)
            if (typeof window !== 'undefined' && window.crypto?.subtle) {
                const msgBuffer = new TextEncoder().encode(components);
                const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
                const hashArray = Array.from(new Uint8Array(hashBuffer));
                this.machineHash = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
            } else {
                this.machineHash = 'fallback-machine';
            }
        } catch (err) {
            logger.warn('[Analytics] Failed to generate machine hash', err);
            this.machineHash = 'fallback-machine';
        }
    }

    private routeEvent(eventName: AnalyticsEvent): { table: string, type: 'behavior' | 'performance' | 'error' | 'ai' } {
        // Only real system exceptions, network errors, and database failures go to telemetry_errors.
        // Form validation errors represent expected user input friction and are routed to analytics_behavior.
        const errors = new Set(['error', 'exception', 'save_map_error']);
        const performance = new Set(['web_vital']);
        // AI events will be mapped here in the future

        if (errors.has(eventName)) return { table: 'telemetry_errors', type: 'error' };
        if (performance.has(eventName)) return { table: 'analytics_performance', type: 'performance' };
        return { table: 'analytics_behavior', type: 'behavior' };
    }

    private async syncToSupabase(eventName: AnalyticsEvent, params?: Record<string, unknown>) {
        if (!this.machineHash) await this.initMachineHash();

        try {
            const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

            // Defensive Gate: Prevent local development events from polluting production Supabase tables
            if (isLocal && !import.meta.env.VITE_ENABLE_LOCAL_ANALYTICS) {
                if (import.meta.env.DEV) {
                    console.debug(`[Analytics Local Bypass] ${eventName}:`, params);
                }
                return;
            }

            // Finalize the payload. We keep it focused and high-signal.
            const {
                _timestamp_utc: _t, // Already in 'created_at' and 'metadata'
                _current_route: _r, // Already in 'metadata'
                _app_version: _v,   // Already in 'metadata'
                payload: explicitPayload, // Support existing patterns passing a sub-payload
                ...cleanParams
            } = (params || {}) as Record<string, unknown>;

            // Merge any explicit payload with the remaining sanitized params
            const finalPayload = {
                ...(typeof explicitPayload === 'object' ? explicitPayload : {}),
                ...cleanParams
            };

            // New Segregated Telemetry Route
            const route = this.routeEvent(eventName);

            const attribution = this.getAttribution();

            // Payload Minimization: No heavy device/geo data duplicated on every row
            const newContext = {
                ...finalPayload,
                current_route: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
                app_version: import.meta.env.VITE_APP_VERSION || 'unknown',
                is_local_env: isLocal,
                is_bot: this.isBot(),
                ...(attribution && Object.keys(attribution).length > 0 ? { attribution } : {})
            };

            let newInsert;
            if (route.type === 'error') {
                newInsert = supabase.from('telemetry_errors').insert({
                    user_id: this.currentUserId,
                    machine_hash: this.machineHash || 'unknown',
                    error_message: String(finalPayload.message || finalPayload.error_message || eventName),
                    stack_trace: String(finalPayload.error_stack || finalPayload.stack || ''),
                    context: newContext as Json
                });
            } else if (route.type === 'performance') {
                newInsert = supabase.from('analytics_performance').insert({
                    user_id: this.currentUserId,
                    machine_hash: this.machineHash || 'unknown',
                    metric_name: String(finalPayload.name || eventName),
                    value: Number(finalPayload.value || 0),
                    context: newContext as Json
                });
            } else {
                // Default fallback is behavior
                newInsert = supabase.from('analytics_behavior').insert({
                    user_id: this.currentUserId,
                    machine_hash: this.machineHash || 'unknown',
                    action: eventName,
                    context: newContext as Json
                });
            }

            // Write Execution (Non-blocking)
            const result = await newInsert;

            // Development logging for visibility into failed writes
            if (import.meta.env.DEV && result.error) {
                console.error(`[Analytics] Supabase Sync Error:`, result.error);
            }
        } catch (err) {
            // Critical failure in sync shouldn't crash calculations
            if (import.meta.env.DEV) {
                console.error('[Analytics] Supabase Sync Exception:', err);
            }
        }
    }

    public track(eventName: AnalyticsEvent, params?: Record<string, unknown>) {
        if (typeof window === 'undefined') return;

        const now = Date.now();

        // --- Session Restoration Gate ---
        // If 30 minutes have passed since the last tracked action, we consider this a new session.
        // Only runs if user has granted analytics consent (GDPR guard).
        const analyticsConsented = getPreferences().analytics;

        const lastActive = analyticsConsented
            ? parseInt(localStorage.getItem('spirit_last_active') || '0', 10)
            : 0;
        const isNewSession = analyticsConsented && (now - lastActive) > 30 * 60 * 1000;

        // Prevent infinite loops: if we are already tracking a login/sign_up, don't prepend another one.
        if (isNewSession && eventName !== 'login' && eventName !== 'sign_up') {
            // Prepend a session restore login
            this.track('login', { method: 'session', is_restore: true });
        }

        // Only stamp last-active if analytics consent is granted
        if (analyticsConsented) {
            localStorage.setItem('spirit_last_active', now.toString());
        }
        // --------------------------------

        // Lightweight Deduplication
        const metricSubKey = eventName === 'web_vital' && params?.name ? `:${params.name}` : '';
        const cacheKey = `${eventName}${metricSubKey}|${this.currentUserId || this.machineHash}`;
        const lastTracked = this.eventCache.get(cacheKey);

        if (lastTracked && (now - lastTracked) < this.CACHE_TTL) {
            if (import.meta.env.DEV) {
                // eslint-disable-next-line no-console
                console.log(`[Analytics] Deduplicated redundant event: ${eventName}${metricSubKey}`);
            }
            return;
        }
        this.eventCache.set(cacheKey, now);

        // 1. Traditional Adapter logic (Firebase)
        this.adapter.trackEvent(eventName, params);


        // 2. New Granite Sync (Supabase) - Async, don't wait
        this.syncToSupabase(eventName, params);
    }

    public error(error: Error, context?: Record<string, unknown>) {
        this.adapter.trackError(error, context);

        // Ensure the actual error message isn't squashed if the context also provides a generic "message" (like "Query failed")
        const ctx = { ...context };
        if (ctx.message && ctx.message !== error.message) {
            ctx.context_message = ctx.message;
        }

        this.syncToSupabase('exception' as AnalyticsEvent, {
            message: error.message,
            error_stack: error.stack?.substring(0, 500),
            ...ctx
        });
    }

    public identify(userId: string | null, traits?: Record<string, unknown>) {
        this.currentUserId = userId;
        this.adapter.identifyUser(userId, traits);
    }
}

const adapter = new FirebaseAnalyticsAdapter();
const service = new AnalyticsService(adapter);

// Inject service into logger to break circular dependency
if (logger && 'setTracker' in logger && typeof logger.setTracker === 'function') {
    logger.setTracker(service);
}

export const analyticsService = service;
